import type {
  LengthInput,
  LengthUnit,
  LengthValue,
  MarginsInput,
  PageMargins,
  PageOrientation,
  PageSize,
  PageSizePreset,
  PrintJobSnapshot
} from "./types";

const MILLIMETERS_PER_INCH = 25.4;
const PIXELS_PER_INCH = 96;
const POINTS_PER_INCH = 72;

const PAGE_PRESETS: Record<PageSizePreset, PageSize> = {
  A3: { name: "A3", width: { value: 297, unit: "mm" }, height: { value: 420, unit: "mm" } },
  A4: { name: "A4", width: { value: 210, unit: "mm" }, height: { value: 297, unit: "mm" } },
  A5: { name: "A5", width: { value: 148, unit: "mm" }, height: { value: 210, unit: "mm" } },
  Letter: { name: "Letter", width: { value: 8.5, unit: "in" }, height: { value: 11, unit: "in" } },
  Legal: { name: "Legal", width: { value: 8.5, unit: "in" }, height: { value: 14, unit: "in" } }
};

// Normalize all accepted length inputs into a structured unit/value pair so the
// rest of the pipeline does not need to branch on input shape repeatedly.
export function normalizeLength(length: LengthInput, fallbackUnit: LengthUnit = "mm"): LengthValue {
  if (typeof length === "number") {
    return { value: length, unit: fallbackUnit };
  }

  if (typeof length === "string") {
    const match = length.trim().match(/^(-?\d+(?:\.\d+)?)(mm|px|pt|cm|in)$/i);

    if (!match) {
      throw new Error(`Invalid length string: ${length}`);
    }

    const value = match[1];
    const unit = match[2];

    if (!value || !unit) {
      throw new Error(`Invalid length string: ${length}`);
    }

    return {
      value: Number.parseFloat(value),
      unit: unit.toLowerCase() as LengthUnit
    };
  }

  return length;
}

export function toCssLength(length: LengthInput, fallbackUnit: LengthUnit = "mm"): string {
  const normalized = normalizeLength(length, fallbackUnit);
  return `${normalized.value}${normalized.unit}`;
}

// Convert physical print units to CSS pixel assumptions so preview estimation
// can reason about printable space in the same coordinate system as the DOM.
export function toPixels(length: LengthInput): number {
  const normalized = normalizeLength(length);

  switch (normalized.unit) {
    case "px":
      return normalized.value;
    case "mm":
      return (normalized.value / MILLIMETERS_PER_INCH) * PIXELS_PER_INCH;
    case "cm":
      return ((normalized.value * 10) / MILLIMETERS_PER_INCH) * PIXELS_PER_INCH;
    case "in":
      return normalized.value * PIXELS_PER_INCH;
    case "pt":
      return (normalized.value / POINTS_PER_INCH) * PIXELS_PER_INCH;
    default:
      return normalized.value;
  }
}

export function resolvePageSize(pageSize: PageSizePreset | PageSize = "A4"): PageSize {
  if (typeof pageSize === "string") {
    return PAGE_PRESETS[pageSize];
  }

  return pageSize;
}

export function normalizeMargins(margins: MarginsInput = {}): PageMargins {
  return {
    top: normalizeLength(margins.top ?? "12mm"),
    right: normalizeLength(margins.right ?? margins.left ?? "12mm"),
    bottom: normalizeLength(margins.bottom ?? margins.top ?? "12mm"),
    left: normalizeLength(margins.left ?? margins.right ?? "12mm")
  };
}

// Normalize page geometry once so preview estimation and CSS generation use the
// same oriented page dimensions and printable content area assumptions.
export function orientPageSize(pageSize: PageSize, orientation: PageOrientation): PageSize {
  if (orientation === "landscape") {
    return {
      ...pageSize,
      width: pageSize.height,
      height: pageSize.width
    };
  }

  return pageSize;
}

export function getPrintableContentHeightPx(snapshot: PrintJobSnapshot): number {
  const oriented = orientPageSize(snapshot.pageSize, snapshot.orientation);
  const pageHeight = toPixels(oriented.height);
  const marginTop = toPixels(snapshot.margins.top);
  const marginBottom = toPixels(snapshot.margins.bottom);
  return Math.max(0, pageHeight - marginTop - marginBottom);
}

export function getPrintableContentWidthPx(snapshot: PrintJobSnapshot): number {
  const oriented = orientPageSize(snapshot.pageSize, snapshot.orientation);
  const pageWidth = toPixels(oriented.width);
  const marginLeft = toPixels(snapshot.margins.left);
  const marginRight = toPixels(snapshot.margins.right);
  return Math.max(0, pageWidth - marginLeft - marginRight);
}
